import { getConditionLabel } from "@features/ax/utils";
import { GenericTable } from "@shared/components/GenericTable";
import { HoverRevealText } from "@shared/components/HoverRevealText";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { TableAction, TableColumn } from "@shared/types/table";
import { Badge } from "@shared/ui/badge";
import { PAGE_SIZE } from "@shared/utils/constants";
import { Eye } from "lucide-react";
import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useGetAllAssets } from "../../hooks";
import { Asset } from "../../types";
import { AssetFilterPanel } from "./assetFilterPanel";

interface AssetFilters {
  status?: number[];
  type?: string;
  remote_utility_id?: number;
  page?: number;
  limit?: number;
  search?: string;
}

const getRiskColor = (score: number) => {
  if (score >= 70) return "bg-red-100 text-red-800";
  if (score >= 40) return "bg-yellow-100 text-yellow-800";
  return "bg-green-100 text-green-800";
};

export const ManufacturerAssets: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const remoteUtilityId = useRemoteUtilityId();
  const [page, setPage] = useState(1);

  // Simple state management for filters
  const [filters, setFilters] = useState<AssetFilters>({
    page: 1,
    limit: PAGE_SIZE,
    remote_utility_id: Number(remoteUtilityId),
    status: [],
    type: "",
    search: "",
  });

  const getAssetStatusColor = (status: string) => {
    switch (status) {
      case "Operational":
        return "bg-green-100 text-green-800";
      case "Under Maintenance":
        return "bg-yellow-100 text-yellow-800";
      case "Retired":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getConditionColor = (score: number) => {
    if (score >= 80) return "text-green-600 font-semibold";
    if (score >= 60) return "text-yellow-600 font-semibold";
    return "text-red-600 font-semibold";
  };

  const columns: TableColumn<Asset>[] = useMemo(
    () => [
      {
        key: "assetNo",
        header: "Asset ID",
        type: "text",
      },
      { key: "assetName", header: "Asset Name", type: "text" },
      { key: "assetTypeDisplay", header: "Asset Type", type: "text" },
      {
        key: "location",
        header: "Location",
        type: "custom",
        render: (value) => <HoverRevealText text={value} />,
      },
      {
        key: "conditionScores",
        header: "Condition Score",
        type: "custom",
        render: (value: number) => (
          <div className="flex items-center gap-2">
            <span className="font-medium">{value}</span>
            <Badge
              variant="secondary"
              className={getConditionLabel(value).color}
            >
              {getConditionLabel(value).label}
            </Badge>
          </div>
        ),
      },
      {
        key: "riskScore",
        header: "Risk Score",
        type: "custom",
        render: (value: number, row: any) => (
          <div className="flex items-center gap-2">
            <span className="font-medium">{value}</span>
            <Badge variant="secondary" className={getRiskColor(value)}>
              {row.riskStatus}
            </Badge>
          </div>
        ),
        headerTooltip:
          "The overall risk this asset poses, combining its likelihood of failure with the consequences of that failure.",
      },
      {
        key: "statusDisplay",
        header: "Status",
        type: "custom",
        render: (value) => (
          <Badge className={getAssetStatusColor(value as string)}>
            {value}
          </Badge>
        ),
      },
    ],
    []
  );

  const actions: TableAction<Asset>[] = useMemo(
    () => [
      {
        label: "View Asset Details",
        onClick: (item) =>
          navigate(`/ax/asset-registry/assets/view/${item.id}`),
        icon: <Eye className="h-4 w-4 mr-2" />,
      },
    ],
    [navigate]
  );

  // Simple filter handlers
  const handleApplyFilters = (newFilters: Partial<AssetFilters>) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
      page: 1, // Reset to page 1 when applying filters
    }));
    setPage(1);
  };

  const handleResetFilters = () => {
    setFilters({
      page: 1,
      limit: PAGE_SIZE,
      remote_utility_id: Number(remoteUtilityId),
      status: [],
      type: "",
      search: "",
    });
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setFilters((prev) => ({
      ...prev,
      page: newPage,
    }));
  };

  const handleSearch = (searchTerm: string) => {
    setFilters((prev) => ({
      ...prev,
      search: searchTerm,
      page: 1, // Reset to page 1 when searching
    }));
    setPage(1);
  };

  const { data: assets } = useGetAllAssets(Number(id), filters);

  const data = assets?.results;
  const totalItems = assets?.count || 0;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);

  const pagination = useMemo(
    () => ({
      currentPage: page,
      totalPages,
      totalItems,
      itemsPerPage: PAGE_SIZE,
      startItem: (page - 1) * PAGE_SIZE + 1,
      endItem: Math.min(page * PAGE_SIZE, totalItems),
    }),
    [page, totalPages, totalItems]
  );

  return (
    <GenericTable
      data={data}
      columns={columns}
      rowKey="id"
      searchPlaceholder="Search by Asset ID, Asset Name..."
      searchParamName="query"
      searchDebounceMs={300}
      onSearch={handleSearch}
      pagination={pagination}
      onPageChange={handlePageChange}
      actions={actions}
      actionsType="dropdown"
      filterPanel={
        <AssetFilterPanel
          currentFilters={filters}
          onApplyFilters={handleApplyFilters}
          onResetFilters={handleResetFilters}
        />
      }
    />
  );
};
